import { IssueBanner } from '@/components/Chat/IssueBanner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { IndeterminateLoadingBar } from '@/components/ui/IndeterminateLoadingBar'
import {
  type FillApplicationState,
  hasFilesReading,
  IntakeMode,
  IntakeRowStatus,
  type IntakeSummary,
} from '@/models/fillApplication'
import { ReadingRow } from './ReadingRow'
import { StopReadingButton } from './StopReadingButton'
import { useDismissFileWarnings } from './useDismissFileWarnings'

interface ReadingPanelProps {
  orgId: string
  state: FillApplicationState
}

function tally(summary: IntakeSummary): string {
  const failed = summary.failed > 0 ? `, ${summary.failed} couldn't be read` : ''
  const stopped = summary.abandoned > 0 ? `, ${summary.abandoned} stopped` : ''
  return `${summary.done} of ${summary.total} read${failed}${stopped}.`
}

/** Every file on the application, newest first, with what they all added and flagged. */
export function ReadingPanel({ orgId, state }: ReadingPanelProps) {
  const summary = state.filesSummary
  const reading = hasFilesReading(state)
  const dismissWarnings = useDismissFileWarnings(orgId)

  return (
    <Card className="gap-4">
      {/* -mt-6 reaches the card's top edge, as on the confirm step's cards. */}
      {reading && <IndeterminateLoadingBar className="-mt-6 mb-2 rounded-t-md" />}
      <CardHeader>
        <CardTitle>{reading ? 'Reading the files' : 'Files'}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <IssueBanner variant="warning" lines={summary?.warnings ?? []} onDismiss={dismissWarnings} />
        {summary && (
          <div className="flex flex-col gap-1 text-sm">
            <span>{tally(summary)}</span>
            {summary.foundSentence && <span className="text-ink-500">{summary.foundSentence}</span>}
          </div>
        )}
        <ul className="flex flex-col gap-3">
          {state.files.map((row) => (
            <ReadingRow
              key={row.id}
              documentId={row.documentId}
              fileName={row.fileName}
              title={row.title}
              status={row.status}
              kinds={row.kinds}
              stale={row.stale}
              stopReason={row.status === IntakeRowStatus.ABANDONED ? row.error : undefined}
              fromCustomer={row.mode === IntakeMode.CUSTOMER_UPLOAD}
            />
          ))}
        </ul>
        {reading && (
          <div className="flex flex-wrap gap-2">
            <StopReadingButton orgId={orgId} rows={state.inFlight.filter((row) => !row.stale)} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
